import React, { useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";

const POPUP_MAX_WIDTH = 420;
const POPUP_MAX_HEIGHT = 260;

/**
 * Text clamped by characters, not space: renders up to `charLimit` characters,
 * wrapping over at most three lines. Only when the text exceeds the limit (or
 * still overflows three lines) does it ellipsize, and hovering then opens a
 * portal popup with the full text — sized to the content, capped at 420×260,
 * scrollable beyond that. The popup stays open while hovered.
 * `maxWidth` is only needed inside auto-layout tables, where an unbounded cell
 * would otherwise stretch to the unwrapped text width.
 */
const TruncatedCell = ({ text, maxWidth = "100%", charLimit = 300, style = {} }) => {
    const textRef = useRef(null);
    const hideTimer = useRef(null);
    const [popupPos, setPopupPos] = useState(null);

    const str = String(text ?? "");
    const isSliced = str.length > charLimit;
    const shown = isSliced ? `${str.slice(0, charLimit).trimEnd()}…` : str;

    useEffect(() => () => clearTimeout(hideTimer.current), []);

    const openPopup = () => {
        clearTimeout(hideTimer.current);
        const el = textRef.current;
        if (!el) return;
        // popup only when cut by the char limit or visually clamped past 3 lines
        if (!isSliced && el.scrollHeight <= el.clientHeight + 1) return;
        const rect = el.getBoundingClientRect();
        const left = Math.max(8, Math.min(rect.left, window.innerWidth - POPUP_MAX_WIDTH - 16));
        const noRoomBelow = rect.bottom + POPUP_MAX_HEIGHT + 12 > window.innerHeight;
        const openUp = noRoomBelow && rect.top > window.innerHeight - rect.bottom;
        setPopupPos({ left, top: openUp ? rect.top - 6 : rect.bottom + 6, openUp });
    };

    const scheduleClose = () => {
        clearTimeout(hideTimer.current);
        hideTimer.current = setTimeout(() => setPopupPos(null), 150);
    };

    const cancelClose = () => clearTimeout(hideTimer.current);

    return (
        <>
            <span
                ref={textRef}
                onMouseEnter={openPopup}
                onMouseLeave={scheduleClose}
                style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    maxWidth,
                    ...style,
                }}
            >
                {shown}
            </span>
            {popupPos && ReactDOM.createPortal(
                <div
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                    style={{
                        position: "fixed",
                        left: popupPos.left,
                        top: popupPos.top,
                        transform: popupPos.openUp ? "translateY(-100%)" : "none",
                        zIndex: 10050,
                        width: "max-content",
                        maxWidth: POPUP_MAX_WIDTH,
                        maxHeight: POPUP_MAX_HEIGHT,
                        overflowY: "auto",
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: 8,
                        boxShadow: "0 8px 24px rgba(15,23,42,0.16)",
                        padding: "10px 12px",
                        fontSize: 12.5,
                        lineHeight: 1.55,
                        color: "#1e293b",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                    }}
                >
                    {str}
                </div>,
                document.body
            )}
        </>
    );
};

export default TruncatedCell;
